import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CrosshairIcon, XIcon } from 'lucide-react';
const legalContent = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que Recopilamos',
      body: 'Recopilamos información que usted nos proporciona directamente al realizar una compra, como su nombre, dirección de correo electrónico e información de pago. No almacenamos datos de tarjetas de crédito; estos son procesados de forma segura por nuestro proveedor de pagos.'
    }, {
      heading: '2. Uso de la Información',
      body: 'Utilizamos su información para procesar transacciones, enviar actualizaciones del producto, proporcionar soporte técnico y mejorar nuestros servicios. No vendemos ni compartimos su información personal con terceros con fines de marketing.'
    }, {
      heading: '3. Cookies y Tecnologías de Seguimiento',
      body: 'Nuestro sitio web puede utilizar cookies y tecnologías similares para mejorar la experiencia del usuario y analizar el tráfico del sitio. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.'
    }, {
      heading: '4. Seguridad de los Datos',
      body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
    }, {
      heading: '5. Sus Derechos',
      body: 'Usted tiene derecho a acceder, corregir o eliminar su información personal. Para ejercer estos derechos, puede contactarnos a través de nuestros canales de soporte.'
    }, {
      heading: '6. Cambios en esta Política',
      body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de la última actualización.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los Términos',
      body: 'Al adquirir y utilizar el Trading Sniper Panel, usted acepta estos términos y condiciones en su totalidad. Si no está de acuerdo con alguno de estos términos, no debe utilizar nuestro software.'
    }, {
      heading: '2. Licencia de Uso',
      body: 'Se le otorga una licencia personal, no transferible e intransferible para utilizar el Sniper Panel en sus propias cuentas de trading en MetaTrader 5. Queda prohibida la redistribución, reventa o ingeniería inversa del software.'
    }, {
      heading: '3. Descargo de Responsabilidad sobre Riesgos',
      body: 'El trading de divisas, índices y otros instrumentos financieros conlleva un alto nivel de riesgo y puede no ser adecuado para todos los inversores. El rendimiento pasado no es indicativo de resultados futuros. Usted es el único responsable de sus decisiones de trading.'
    }, {
      heading: '4. Sin Garantía de Resultados',
      body: 'El Sniper Panel es una herramienta de gestión de riesgo y ejecución. No garantizamos ganancias ni resultados específicos. El éxito en el trading depende de múltiples factores, incluyendo las condiciones del mercado y la disciplina del operador.'
    }, {
      heading: '5. Soporte Técnico',
      body: 'Proporcionamos soporte técnico para la instalación y configuración del software. El soporte no incluye asesoramiento financiero, señales de trading ni formación en estrategias de inversión.'
    }, {
      heading: '6. Política de Reembolso',
      body: 'Debido a la naturaleza digital del producto, todas las ventas son finales. No se ofrecen reembolsos una vez que se ha entregado el acceso al software. Asegúrese de revisar toda la información del producto antes de realizar su compra.'
    }, {
      heading: '7. Limitación de Responsabilidad',
      body: 'En ningún caso seremos responsables por pérdidas financieras, daños directos o indirectos que resulten del uso de nuestro software. El usuario asume toda la responsabilidad por el uso del Sniper Panel en sus operaciones de trading.'
    }]
  }
};
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal ? legalContent[activeModal] : null;
  return <>
      <footer className="bg-dark py-12 border-t border-surfaceHighlight" data-id="element-68">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center" data-id="element-69">
          <div className="flex items-center gap-2 mb-6 opacity-50" data-id="element-70">
            <CrosshairIcon className="w-6 h-6 text-white" data-id="element-71" />
            <span className="font-display font-extrabold text-lg tracking-wider text-white" data-id="element-72">
              SNIPER PANEL
            </span>
          </div>

          <div className="flex items-center gap-6 mb-6" data-id="element-73">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 text-sm hover:text-primary transition-colors cursor-pointer" data-id="element-74">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-75">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 text-sm hover:text-primary transition-colors cursor-pointer" data-id="element-76">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-gray-600 text-xs max-w-2xl mb-4" data-id="element-77">
            El trading de divisas, índices y otros activos financieros conlleva
            un alto nivel de riesgo y puede no ser adecuado para todos los
            inversores. El rendimiento pasado no es indicativo de resultados
            futuros.
          </p>

          <div className="text-gray-700 text-xs font-mono" data-id="element-78">
            &copy; {new Date().getFullYear()} Sniper Panel. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-79">
        {activeModal && content && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-80">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-81"></div>

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="relative glass-panel rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-82">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-surfaceHighlight shrink-0" data-id="element-83">
                <h2 className="font-display font-bold text-lg text-white uppercase tracking-wider" data-id="element-84">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-white transition-colors p-1" data-id="element-85">
                  <XIcon className="w-5 h-5" data-id="element-86" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto p-6 space-y-6" data-id="element-87">
                {content.sections.map((section, i) => <div key={i} data-id="element-88">
                    <h3 className="font-display text-sm font-bold text-primary mb-2 uppercase tracking-wide" data-id="element-89">
                      {section.heading}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-90">
                      {section.body}
                    </p>
                  </div>)}

                <p className="text-gray-600 text-xs pt-4 border-t border-surfaceHighlight" data-id="element-91">
                  Última actualización: Abril 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}